import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AnimatedBackground } from "@/components/AnimatedBackground";
import { ArrowRight, Brain, Rocket, Target, Users, Zap, Award } from "lucide-react";
import { Header } from "@/components/Header";
import { CompanyLogo } from "@/components/CompanyLogo";

const companies = [
  { 
    id: 'google', 
    name: 'Google', 
    logo: '/assets/company-logos/google.svg',
    logoLight: '/assets/company-logos/google.svg'
  },
  { 
    id: 'meta', 
    name: 'Meta', 
    logo: '/assets/company-logos/meta.svg',
    logoLight: '/assets/company-logos/meta.svg'
  },
  { 
    id: 'apple', 
    name: 'Apple', 
    logo: '/assets/company-logos/apple.svg',
    logoLight: '/assets/company-logos/apple-light.svg',
    useLightInDark: true
  },
  { 
    id: 'amazon', 
    name: 'Amazon', 
    logo: '/assets/company-logos/amazon.svg',
    logoLight: '/assets/company-logos/amazon-light.svg',
    useLightInDark: true
  },
  { 
    id: 'netflix', 
    name: 'Netflix', 
    logo: '/assets/company-logos/netflix.svg',
    logoLight: '/assets/company-logos/netflix.svg'
  },
];

const stats = [
  { value: '98%', label: 'Success Rate' },
  { value: '50K+', label: 'Interviews Conducted' },
  { value: '200+', label: 'Companies' },
  { value: '24/7', label: 'AI Availability' },
];

const FeatureCard = ({ icon: Icon, title, description }: { icon: any, title: string, description: string }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    whileHover={{ scale: 1.02 }}
    transition={{ duration: 0.5 }}
    className="glass p-6 rounded-xl hover:shadow-xl hover:shadow-primary/10 transition-all duration-300"
  >
    <div className="relative">
      <motion.div 
        className="absolute -inset-1 rounded-lg bg-gradient-to-r from-primary/20 to-blue-600/20 blur-lg"
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.5, 0.8, 0.5]
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <div className="relative">
        <Icon className="w-10 h-10 text-primary mb-4" />
      </div>
    </div>
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-muted-foreground">{description}</p>
  </motion.div>
);

const steps = [
  {
    step: 1,
    title: "Select Company & Experience",
    description: "Choose your target company and experience level to get a tailored interview experience"
  },
  {
    step: 2,
    title: "Upload Resume",
    description: "Upload your resume to help our AI understand your background and tailor questions accordingly"
  },
  {
    step: 3,
    title: "Start Interview",
    description: "Begin your AI interview session with real-time voice interaction and feedback"
  }
];

export default function Index() {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 200], [1, 0]);

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-background/50 to-background">
      <AnimatedBackground />
      <Header />
      
      <main className="container mx-auto max-w-4xl px-6 pt-24 pb-16">
        {/* Hero Section with Parallax */}
        <motion.section 
          className="py-20 text-center"
          style={{ opacity }}
        >
          <motion.h1 
            className="mb-6 bg-gradient-to-r from-primary to-blue-600 dark:from-white dark:to-white/80 bg-clip-text text-5xl font-bold text-transparent sm:text-7xl"
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", damping: 10, stiffness: 100 }}
          >
            Master Your Interviews with AI
          </motion.h1>
          
          <motion.p 
            className="mb-12 text-lg text-muted-foreground dark:text-foreground/80 sm:text-xl max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Practice with our AI interviewer tailored to your target companies. Get real-time feedback, improve your responses, and boost your confidence.
          </motion.p>

          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="mb-20"
          >
            <Button
              size="lg"
              className="relative overflow-hidden bg-gradient-to-r from-primary to-blue-600 text-lg transition-all duration-300 hover:shadow-lg hover:shadow-primary/50 group"
              onClick={() => navigate("/select-company")}
            >
              Start Interview
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              <motion.span
                className="absolute inset-0 bg-white/20"
                animate={{
                  transform: ["translateX(-100%)", "translateX(100%)"],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  repeatType: "loop",
                }}
              />
            </Button>
          </motion.div>
        </motion.section>

        {/* Stats Section with Stagger */}
        <section className="py-20">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.2
                }
              }
            }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
                className="text-center"
              >
                <motion.div 
                  className="text-3xl font-bold text-primary mb-2"
                  whileInView={{ scale: [0.8, 1.2, 1] }}
                  transition={{ duration: 0.6 }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* Company Logos with Slide */}
        <section className="py-20">
          <motion.h2 
            className="text-xl font-semibold mb-8 text-center text-muted-foreground"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            Trusted by candidates from
          </motion.h2>
          <motion.div 
            className="grid grid-cols-3 md:grid-cols-5 gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1
                }
              }
            }}
          >
            {companies.map((company) => (
              <motion.div
                key={company.id}
                variants={{
                  hidden: { opacity: 0, x: -20 },
                  visible: { opacity: 1, x: 0 }
                }}
              >
                <CompanyLogo 
                  logo={company.logo} 
                  logoLight={company.logoLight} 
                  name={company.name} 
                  useLightInDark={company.useLightInDark}
                />
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* Features Grid */}
        <section className="py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon={Brain}
              title="AI-Powered Practice"
              description="Get realistic interview experience with our advanced AI interviewer"
            />
            <FeatureCard
              icon={Target}
              title="Company-Specific"
              description="Practice with questions tailored to your target company"
            />
            <FeatureCard
              icon={Rocket}
              title="Instant Feedback"
              description="Receive real-time feedback to improve your responses"
            />
          </div>
        </section>

        {/* How It Works Section with Fade and Slide */}
        <section className="py-20">
          <motion.h2 
            className="text-4xl font-bold mb-12 text-center bg-gradient-to-r from-primary to-blue-600 dark:from-white dark:to-white/80 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            How It Works
          </motion.h2>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.2
                }
              }
            }}
          >
            {steps.map((item, index) => (
              <motion.div
                key={item.step}
                variants={{
                  hidden: { opacity: 0, x: -20 },
                  visible: { opacity: 1, x: 0 }
                }}
                className="text-center p-6 rounded-xl backdrop-blur-sm border border-border/50"
              >
                <motion.div 
                  className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"
                  whileInView={{
                    scale: [1, 1.2, 1],
                    rotate: [0, 360]
                  }}
                  transition={{ duration: 0.6 }}
                >
                  <span className="text-2xl font-bold text-primary">{item.step}</span>
                </motion.div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>
      </main>

      {/* Footer with Fade */}
      <motion.footer 
        className="border-t border-border/50 bg-background/50 backdrop-blur-lg"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="container mx-auto px-6 py-8">
          <div className="text-center text-muted-foreground">
            <p>© 2025 VoiseIQ. All rights reserved.</p>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}